import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { reverse } from 'named-urls'
import PropTypes from 'prop-types'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Button } from '@material-ui/core'

import { MenuButton, ConfirmationDialog } from 'components'
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from 'components/Table'
import QuestionnaireStatusChip from '../QuestionnaireStatusChip'

import { routes } from 'Routes'
import constants from 'constants/index'
import { usePermissions } from 'hooks'
import * as service from 'service'
import useSnackbar from 'hooks/useSnackbar'
import helpers from 'helpers'

const QuestionnaireTable = ({ questionnaires, refresh = () => {} }) => {
  const history = useHistory()
  const permissions = usePermissions()
  const snackbar = useSnackbar()
  const [openResetDialog, setOpenResetDialog] = useState(false)
  const [questionnaireToReset, setQuestionnaireToReset] = useState(null)

  const hasPermissionToEditQuestionnaire = permissions.permitted(
    constants.permissions.QUESTIONNAIRE_BY_COMPANY.EDIT,
  )

  const navigateTo = (route, questionnaireId) => {
    history.push(reverse(route, { questionnaireId: questionnaireId }))
  }

  const handleNavigateToEditPage = (questionnaireId) => {
    navigateTo(routes.questionnaires.edit, questionnaireId)
  }

  const handleNavigateToShowPage = (questionnaireId) => {
    navigateTo(routes.questionnaires.show, questionnaireId)
  }

  const handleResetQuestionnaire = (questionnaire) => {
    setQuestionnaireToReset(questionnaire)
    setOpenResetDialog(true)
  }

  const confirmResetQuestionnaire = async () => {
    try {
      await service.dponet.questionnaires.reset({
        questionnaireId: questionnaireToReset.id,
      })

      snackbar.open({
        message: 'Respostas do questionário resetadas com sucesso!',
        variant: 'success',
      })

      refresh()
    } catch (error) {
      snackbar.open({
        variant: 'error',
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
      })
    }

    setOpenResetDialog(false)
    setQuestionnaireToReset(null)
  }

  return (
    <PerfectScrollbar
      options={{ wheelPropagation: false, useBothWheelAxes: true }}
    >
      <Table emptyMessage="Nenhum questionário encontrado" noWrap>
        <TableHead>
          <TableRow>
            <TableCell width="5%">ID</TableCell>
            <TableCell width="20%">Questionário</TableCell>
            <TableCell width="20%">Empresa</TableCell>
            <TableCell width="10%">Documento</TableCell>
            <TableCell width="10%">Status</TableCell>
            <TableCell width="10%">Visível</TableCell>
            <TableCell width="10%">Respondida</TableCell>
            <TableCell width="10%">Nº de questões</TableCell>
            <TableCell width="5%" align="right">
              Ações
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {questionnaires.map((questionnaire) => (
            <TableRow key={questionnaire.id}>
              <TableCell>{questionnaire.id}</TableCell>
              <TableCell>{questionnaire.name}</TableCell>
              <TableCell>{questionnaire.company?.name || '-'}</TableCell>
              <TableCell>{questionnaire.company?.document || '-'}</TableCell>
              <TableCell>
                <QuestionnaireStatusChip status={questionnaire.status} />
              </TableCell>
              <TableCell>{questionnaire.visible ? 'Sim' : 'Não'}</TableCell>
              <TableCell>{questionnaire.answerPercentage}%</TableCell>
              <TableCell>{questionnaire.totalQuestions}</TableCell>
              <TableCell disableTooltip align="right">
                <MenuButton>
                  {hasPermissionToEditQuestionnaire && (
                    <Button
                      color="secondary"
                      onClick={() => handleNavigateToEditPage(questionnaire.id)}
                      fullWidth
                      size="small"
                    >
                      Editar
                    </Button>
                  )}
                  <Button
                    color="secondary"
                    onClick={() => handleNavigateToShowPage(questionnaire.id)}
                    fullWidth
                    size="small"
                  >
                    Visualizar
                  </Button>
                  <Button
                    color="secondary"
                    onClick={() => handleResetQuestionnaire(questionnaire)}
                    fullWidth
                    size="small"
                  >
                    Resetar respostas
                  </Button>
                </MenuButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ConfirmationDialog
        open={openResetDialog}
        setOpen={setOpenResetDialog}
        message={`Tem certeza que deseja resetar todas as respostas do questionário "${questionnaireToReset?.name}"? Esta ação não pode ser desfeita!`}
        cancelButtonText="Cancelar"
        buttonText="Resetar"
        actionAcceptButton={confirmResetQuestionnaire}
      />
    </PerfectScrollbar>
  )
}

QuestionnaireTable.propTypes = {
  questionnaires: PropTypes.array,
  refresh: PropTypes.func,
}

QuestionnaireTable.defaultProps = {
  questionnaires: [],
  refresh: () => {},
}

export default QuestionnaireTable
